import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Plus,
  FileText,
  Calendar,
  Clock,
  User,
  GraduationCap,
  Trash2,
  Edit3,
  Play,
  MoreVertical,
  AlertCircle,
  CheckCircle,
  Loader,
  XCircle,
  ChevronDown,
  ChevronUp,
  X,
  Brain,
  Wand2,
  Sparkles,
  RefreshCw,
  BookOpen,
  Upload,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { Exam, CourseMaterial } from '../types/exam.types';
import { examApi } from '../services/examApi';
import { coursesApi, Course, authApi } from '../services/api';
import { analytics } from '../services/analytics';
import { MixedText } from '../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { DashboardContainer } from './layout/PageContainer';

const ExamListView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const courseIdNum = parseInt(courseId || '0');
  
  console.log('ExamListView component loaded with courseId:', courseId, 'courseIdNum:', courseIdNum);

  // State
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [course, setCourse] = useState<any>(null);
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [isVip, setIsVip] = useState(false);
  
  // Course switching state
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  
  
  // Delete states
  const [deletingExam, setDeletingExam] = useState<number | null>(null);

  // Edit states
  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [editExamName, setEditExamName] = useState('');

  // Modal states
  const [showCreationModal, setShowCreationModal] = useState(false);

  useEffect(() => {
    console.log('🎭 Modal state changed:', showCreationModal);
    if (showCreationModal) {
      console.log('🎭 Modal should be visible now');
    }
  }, [showCreationModal]);

  useEffect(() => {
    if (courseIdNum) {
      loadData();
      analytics.track('Exam List Viewed', { course_id: courseIdNum });
    }
  }, [courseIdNum]);

  // Load all courses for dropdown
  useEffect(() => {
    loadAllCourses();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCourseDropdown && dropdownButtonRef.current) {
        const target = event.target as Element;
        
        // Check if click is on the button or dropdown
        const isButton = dropdownButtonRef.current.contains(target);
        const isDropdown = target.closest('.course-dropdown-portal');
        
        if (!isButton && !isDropdown) {
          setShowCourseDropdown(false);
        }
      }
    };

    const handleScroll = () => {
      if (showCourseDropdown) {
        setShowCourseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showCourseDropdown]);

  // Periodic status updates
  useEffect(() => {
    if (!courseIdNum) return;

    // Only set up periodic refresh if there are exams that might need updates
    const hasProcessingExams = exams.some(exam => 
      exam.processing_status === 'pending' || 
      exam.processing_status === 'processing'
    );

    if (!hasProcessingExams) return;

    const interval = setInterval(async () => {
      await refreshExamStatuses();
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [courseIdNum, exams]);

  const refreshExamStatuses = async () => {
    try {
      setRefreshingStatuses(true);
      console.log('🔄 Refreshing exam statuses...');
      
      const updatedExams = await examApi.getCourseExams(courseIdNum);
      
      // Only update if there are actual changes to avoid unnecessary re-renders
      const hasChanges = updatedExams.some((updatedExam, index) => {
        const currentExam = exams[index];
        return !currentExam || 
               currentExam.processing_status !== updatedExam.processing_status ||
               currentExam.id !== updatedExam.id;
      });

      if (hasChanges || updatedExams.length !== exams.length) {
        console.log('📊 Status changes detected, updating exams');
        setExams(updatedExams);
      } else {
        console.log('📊 No status changes detected');
      }
    } catch (err) {
      console.error('Error refreshing exam statuses:', err);
      // Don't show error to user for background refresh failures
    } finally {
      setRefreshingStatuses(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading data for course:', courseIdNum);
      
      // Load course first
      let courseData = null;
      try {
        console.log('Loading course data...');
        courseData = await coursesApi.getCourse(courseIdNum);
        console.log('Course data loaded:', courseData);
        setCourse(courseData);
      } catch (err) {
        console.error('Error loading course:', err);
        setError(t('common:errors.loading_failed'));
        return;
      }
      
      // Load user data for VIP status
      try {
        console.log('Loading user data for VIP status...');
        const userData = await authApi.getCurrentUser();
        console.log('User data loaded:', userData);
        setIsVip(userData.is_vip);
      } catch (err) {
        console.error('Error loading user data:', err);
        // Don't fail completely if user data fails to load
        setIsVip(false);
      }
      
      // Load exams and materials separately to isolate issues
      let examsData = [];
      let materialsData = [];
      
      try {
        console.log('Loading exams...');
        examsData = await examApi.getCourseExams(courseIdNum);
        console.log('Exams loaded:', examsData);
        console.log('💾 Detailed exam data with S3 URIs:');
        examsData.forEach((exam, index) => {
          console.log(`  Exam ${index + 1}:`, {
            id: exam.id,
            title: exam.title,
            s3_uri: exam.s3_uri,
            processing_status: exam.processing_status,
            has_s3_uri: !!exam.s3_uri
          });
        });
        setExams(examsData);
      } catch (err) {
        console.error('Error loading exams:', err);
        // Don't fail completely if exams fail to load
        setExams([]);
      }
      
      console.log('All data loaded successfully');
    } catch (err) {
      console.error('Error loading exam data:', err);
      setError(t('common:errors.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  const loadAllCourses = async () => {
    try {
      setCoursesLoading(true);
      const coursesData = await coursesApi.getAllCourses();
      setAllCourses(coursesData);
    } catch (err) {
      console.error('Error loading all courses:', err);
      // Don't fail completely if courses fail to load
      setAllCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleCourseSwitch = (newCourseId: number) => {
    setShowCourseDropdown(false);
    navigate(`/course/${newCourseId}/exams`);
    analytics.track('Course Switched from Exam List', {
      from_course_id: courseIdNum,
      to_course_id: newCourseId
    });
  };

  const handleDropdownToggle = () => {
    if (!showCourseDropdown && dropdownButtonRef.current) {
      const rect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 256) // Minimum 256px width
      });
    }
    setShowCourseDropdown(!showCourseDropdown);
  };

  const formatDate = (dateString: string) => {
    const locale = isRTL ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if study guide is available
  const isStudyGuideAvailable = exams.length > 0 && exams.some(exam => exam.exam_type !== 'ai_generated');


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />;
      case 'processing':
        return <Loader className="w-4 h-4 mr-2 ml-2 text-muted-purple animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />;
      case 'failed':
        return <XCircle className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />;
      default:
        return <AlertCircle className="w-4 h-4 mr-2 ml-2 text-surface-muted" />;
    }
  };

  const getStatusText = (status: string) => {
    return t(`exams:processing.${status}`);
  };


  const handleDeleteExam = async (examId: number, examTitle: string) => {
    if (!window.confirm(t('exams:examList.confirmDelete'))) {
      return;
    }

    setDeletingExam(examId);
    try {
      await examApi.deleteExam(examId);
      setExams(prev => prev.filter(e => e.id !== examId));
      analytics.track('Exam Deleted', {
        course_id: courseIdNum,
        exam_id: examId,
        exam_title: examTitle
      });
    } catch (err) {
      console.error('Error deleting exam:', err);
      setError(t('common:errors.delete_failed'));
      analytics.trackError('Exam Delete Failed', {
        courseId: courseIdNum,
        examId,
        examTitle
      });
    } finally {
      setDeletingExam(null);
    }
  };

  const handleEditExam = (exam: Exam) => {
    setEditingExam(exam);
    setEditExamName(exam.title);
  };

  const handleUpdateExam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingExam || !editExamName.trim()) return;

    try {
      const updatedExam = await examApi.updateExam(editingExam.id, {
        title: editExamName.trim(),
      });
      
      // Track exam update
      analytics.track('Exam Updated', {
        exam_id: editingExam.id,
        old_title: editingExam.title,
        new_title: editExamName.trim(),
        title_changed: editingExam.title !== editExamName.trim(),
      });
      
      // Update the exam in the state
      setExams(prev => prev.map(exam => 
        exam.id === editingExam.id ? updatedExam : exam
      ));
      
      setEditingExam(null);
      setEditExamName('');
    } catch (err) {
      analytics.trackError('Exam Update Failed', { 
        examId: editingExam.id,
        examTitle: editExamName.trim() 
      });
      setError(t('common:errors.update_failed'));
      console.error(err);
    }
  };

  const handleCreateExamClick = () => {
    console.log('🔍 handleCreateExamClick called');
    console.log('📊 Current exams:', exams);
    
    // Check if there are exams with S3 links
    const examsWithFiles = exams.filter(exam => exam.s3_uri);
    
    console.log('📁 Exams with S3 URIs:', examsWithFiles);
    console.log('📈 Count of exams with files:', examsWithFiles.length);
    
    if (examsWithFiles.length > 0) {
      console.log('✅ Showing modal - found exams with S3 URIs');
      // Show modal to choose between upload and AI generation
      setShowCreationModal(true);
    } else {
      console.log('❌ No exams with S3 URIs found, navigating to create');
      // Navigate directly to upload exam
      navigate(`/exams/create?course_id=${courseIdNum}`);
    }
  };


  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibrant-orange"></div>
        </div>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 text-vibrant-orange mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-surface-primary mb-2">{t('common:errors.something_went_wrong')}</h2>
          <p className="text-surface-secondary mb-6">{error}</p>
          <Button
            onClick={loadData}
            variant="primary"
          >
            {t('common:actions.try_again')}
          </Button>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <div className="space-y-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/course/${courseIdNum}`)}
          className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
        >
          {isRTL ? (
            <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
          ) : (
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          )}
          <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
            <MixedText>{t('common:navigation.back_to_course', { courseName: course?.name || t('common:labels.course') })}</MixedText>
          </span>
        </button>

        {/* Page Header */}
        <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
          <CardHeader>
            <div className="flex items-center">
                            {/* Course Switcher - Left side for both languages */}
              <div className={`order-1 mr-6`}>
                <button
                  ref={dropdownButtonRef}
                  onClick={handleDropdownToggle}
                  className={`flex items-center px-3 py-2 text-sm font-medium text-surface-secondary hover:text-surface-primary hover:bg-vibrant-orange/10 rounded-lg transition-colors ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
                  disabled={coursesLoading}
                >
                  <span className={`font-hebrew ${isRTL ? 'text-right' : 'text-right'}`}>
                    <MixedText>
                      {t('exams:examList.switchCourse.label')}
                    </MixedText>
                  </span>
                  {coursesLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : showCourseDropdown ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>

                             <div className={`flex-1 min-w-0 ${isRTL ? 'text-right pr-6 order-2' : 'text-left pl-0 order-2'}`}>
                <CardTitle className={`text-2xl md:text-3xl mb-2 ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>
                    {t('exams:examList.title', { courseName: course?.name })}
                  </MixedText>
                </CardTitle>
                <CardContent className={`text-surface-secondary ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>
                    {t('exams:examPractice.subtitle')}
                  </MixedText>
                </CardContent>
              </div>
                             <div className={`w-16 h-16 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-2xl flex items-center justify-center flex-shrink-0 ${isRTL ? 'ml-6 order-3' : 'ml-6 order-3'}`}>
                <GraduationCap className="w-8 h-8 text-vibrant-orange" />
              </div>
            </div>
          </CardHeader>
        </SpaceCard>


        {/* Course Navigation Buttons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          

          {/* Course Materials Button */}
          <button
            onClick={() => {
              navigate(`/course/${courseId}/materials`);
              analytics.track('Course Materials Accessed from Exam List', {
                course_id: courseIdNum,
                source: 'exam_list_navigation'
              });
            }}
            className={`p-3 md:p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
              bg-gradient-to-br from-muted-purple/80 to-muted-purple/90 
              hover:from-muted-purple/90 hover:to-muted-purple/95
              backdrop-blur-sm border border-muted-purple/30 
              shadow-lg hover:shadow-xl hover:shadow-muted-purple/20
              text-astronaut-white hover:text-white min-h-[100px] md:min-h-[140px]`}
          >
            <img 
              src="/astronaut_papers.png" 
              alt="StudyBel Astronaut with Papers" 
              className={`w-12 h-12 md:w-20 md:h-20 ${isRTL ? 'ml-2 md:ml-3' : 'mr-2 md:mr-3'} filter brightness-110`}
            />
            <div className={`flex flex-col ${isRTL ? 'text-right' : ''}`}>
              <span className="text-lg md:text-2xl font-bold mb-1">
                <MixedText>{t('exams:courseMaterials.navigationTitle', 'Course Materials')}</MixedText>
              </span>
              <span className="text-sm md:text-base opacity-90">
                <MixedText>{t('exams:courseMaterials.navigationSubtitle', 'Reference documents and resources')}</MixedText>
              </span>
            </div>
          </button>

          {/* Study Guide Button */}
          <button
            onClick={() => {
              if (isStudyGuideAvailable) {
                navigate(`/course/${courseId}/study-guide`);
                analytics.track('Study Guide Accessed from Exam List', {
                  course_id: courseIdNum,
                  total_exams: exams.length,
                  source: 'exam_list_navigation'
                });
              }
            }}
            disabled={!isStudyGuideAvailable}
            className={`p-3 md:p-4 rounded-2xl transition-all duration-200 font-hebrew flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} 
              ${isStudyGuideAvailable 
                ? 'bg-gradient-to-br from-vibrant-orange/80 to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/95 backdrop-blur-sm border border-vibrant-orange/30 shadow-lg hover:shadow-xl hover:shadow-vibrant-orange/20 text-astronaut-white hover:text-white hover:scale-105 cursor-pointer'
                : 'bg-gradient-to-br from-vibrant-orange/20 to-vibrant-orange/30 backdrop-blur-sm border border-vibrant-orange/20 shadow-sm text-vibrant-orange/60 cursor-not-allowed'
              } min-h-[100px] md:min-h-[140px]`}
            title={!isStudyGuideAvailable ? t('exams:studyGuide.requiresExams', 'Upload exams to generate study guide') : undefined}
          >
            <img 
              src="/astronaut_roadmap.png" 
              alt="StudyBel Astronaut with Papers" 
              className={`w-12 h-12 md:w-20 md:h-20 ${isRTL ? 'ml-2 md:ml-3' : 'mr-2 md:mr-3'} filter ${isStudyGuideAvailable ? 'brightness-110' : 'brightness-75 opacity-60'}`}
            />
            <div className={`flex flex-col ${isRTL ? 'text-right' : ''}`}>
              <span className="text-lg md:text-2xl font-bold mb-1">
                <MixedText>{t('exams:studyGuide.courseGuideTitle', 'Course Study Guide')}</MixedText>
              </span>
              <span className="text-sm md:text-base opacity-90">
                <MixedText>
                  {isStudyGuideAvailable 
                    ? t('exams:studyGuide.courseGuideSubtitle', 'AI-generated comprehensive guide')
                    : t('exams:studyGuide.uploadExamsFirst', 'Upload exams first')
                  }
                </MixedText>
              </span>
            </div>
          </button>
        </div>

        {/* Exams Section */}
        <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
          <CardHeader className="text-center border-b border-surface-muted/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <CardTitle className={`text-2xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {t('common:labels.exams')}
                </CardTitle>
                {isRTL ? (
                  <MixedText className="text-surface-secondary max-w-2xl mx-auto font-hebrew">
                    {exams.length === 0 
                      ? t('exams:examList.emptyState.description')
                      : `${exams.length} ${exams.length === 1 ? t('common:labels.exam') : t('common:labels.exams')}`
                    }
                  </MixedText>
                ) : (
                  <p className="text-surface-secondary max-w-2xl mx-auto font-hebrew">
                    {exams.length === 0 
                      ? t('exams:examList.emptyState.description')
                      : `${exams.length} ${exams.length === 1 ? t('common:labels.exam') : t('common:labels.exams')}`
                    }
                  </p>
                )}
              </div>
              
              {/* Status refresh indicator */}
              {refreshingStatuses && (
                <div className="flex items-center text-xs text-surface-secondary">
                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  <span className="font-hebrew">
                    <MixedText>{t('common:messages.refreshing')}</MixedText>
                  </span>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent>
            {/* Exams Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 max-w-6xl mx-auto">
              {/* Add Exam Card - Show first (left) for RTL */}
              {isRTL && (
                <SpaceCard
                  constellation="orion"
                  onClick={handleCreateExamClick}
                  className="cursor-pointer border-2 border-dashed border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[240px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
                >
                  <div className="p-6 md:p-8 flex flex-col items-center justify-center">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-vibrant-orange/20 transition-colors duration-300">
                      <Plus className="w-6 h-6 md:w-8 md:h-8 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <CardTitle className={`text-lg md:text-xl mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      🚀 <MixedText>{t('exams:examList.createExam')}</MixedText>
                    </CardTitle>
                    <p className={`text-surface-secondary text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:examList.emptyState.description')}</MixedText>
                    </p>
                  </div>
                </SpaceCard>
              )}

              {/* Existing Exams */}
              {exams.map((exam) => (
                exam.processing_status === 'completed' ? (
                  <SpaceCard
                    key={exam.id}
                    constellation="cassiopeia"
                    className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex flex-col"
                  >
                    <Link
                      to={`/course/${courseIdNum}/exams/${exam.id}/practice`}
                      className="block flex-1"
                    >
                      {/* Exam Header */}
                      <CardHeader>
                        <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                          <div className={`hidden md:flex items-center justify-center w-14 h-14 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-xl flex-shrink-0 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'order-2' : 'order-1'}`}>
                            <GraduationCap className="w-7 h-7 text-vibrant-orange" />
                          </div>
                          <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-2' : 'order-2'}`}>
                            <div className={`flex items-start mb-2 ${isRTL ? 'justify-start flex-row-reverse' : 'justify-between'}`}>
                              <CardTitle className={`text-lg md:text-xl line-clamp-2 group-hover:text-vibrant-orange transition-colors duration-300 flex-1 ${isRTL ? 'text-right' : ''}`}>
                                <MixedText>
                                  {exam.title}
                                </MixedText>
                              </CardTitle>
                              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                {getStatusIcon(exam.processing_status)}
                                <span className={`text-sm font-medium font-hebrew mr-2 ml-2 ${isRTL ? 'text-right' : ''}`}>
                                  <MixedText>{getStatusText(exam.processing_status)}</MixedText>
                                </span>
                              </div>
                            </div>
                            <div className={`flex items-center text-xs text-surface-secondary ${isRTL ? 'justify-end' : ''}`}>
                              <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Calendar className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                <span className="font-hebrew">
                                  <MixedText>{exam.exam_date ? formatDate(exam.exam_date) : formatDate(exam.created_at)}</MixedText>
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {/* Exam Content */}
                      <CardContent>
                        {/* Exam Type Badge */}
                        <div className="mb-4">
                          <span className={`inline-block px-3 py-1 bg-vibrant-orange/10 text-vibrant-orange rounded-full text-sm font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                            <MixedText>{t(`exams:examList.examTypes.${exam.exam_type}`)}</MixedText>
                          </span>
                        </div>

                        {/* Status Display */}
                        <div className="text-center">
                          <div className="inline-flex items-center text-sm text-surface-secondary font-medium group-hover:text-vibrant-orange transition-colors duration-300 font-hebrew">
                            {isRTL ? (
                              <>
                                <span className="mr-2 transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
                                <MixedText>
                                  {t('exams:examList.practice')}
                                </MixedText>
                              </>
                            ) : (
                              <>
                                <MixedText>
                                  {t('exams:examList.practice')}
                                </MixedText>
                                <span className="ml-2 transform group-hover:translate-x-1 transition-transform duration-300">→</span>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Link>
                    
                    {/* Action Bar */}
                    <CardFooter align="center" className="border-t border-surface-muted/20">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditExam(exam);
                          }}
                          className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-blue/10 hover:text-vibrant-blue flex items-center justify-center"
                          title={t('common:actions.edit')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteExam(exam.id, exam.title);
                          }}
                          disabled={deletingExam === exam.id}
                          className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                          title={t('exams:examList.delete')}
                        >
                          {deletingExam === exam.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </CardFooter>
                  </SpaceCard>
                ) : (
                  <SpaceCard
                    key={exam.id}
                    constellation="cassiopeia"
                    className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02] flex flex-col"
                  >
                    {/* Same structure for non-completed exams */}
                    <CardHeader>
                      <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                        <div className={`hidden md:flex items-center justify-center w-14 h-14 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-xl flex-shrink-0 group-hover:shadow-lg transition-shadow duration-300 ${isRTL ? 'order-2' : 'order-1'}`}>
                          <GraduationCap className="w-7 h-7 text-vibrant-orange" />
                        </div>
                        <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-2' : 'order-2'}`}>
                          <div className={`flex items-start mb-2 ${isRTL ? 'justify-start flex-row-reverse' : 'justify-between'}`}>
                            <CardTitle className={`text-lg md:text-xl line-clamp-2 group-hover:text-vibrant-orange transition-colors duration-300 flex-1 ${isRTL ? 'text-right' : ''}`}>
                              🛸 <MixedText>
                                {exam.title}
                              </MixedText>
                            </CardTitle>
                            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                              {getStatusIcon(exam.processing_status)}
                              <span className={`text-sm font-medium font-hebrew mr-2 ml-2 ${isRTL ? 'text-right' : ''}`}>
                                {getStatusText(exam.processing_status)}
                              </span>
                            </div>
                          </div>
                          <div className={`flex items-center text-xs text-surface-secondary ${isRTL ? 'justify-end' : ''}`}>
                            <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Calendar className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                              <span className="font-hebrew">
                                {exam.exam_date ? formatDate(exam.exam_date) : formatDate(exam.created_at)}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="mb-4">
                        <span className={`inline-block px-3 py-1 bg-vibrant-orange/10 text-vibrant-orange rounded-full text-sm font-medium font-hebrew ${isRTL ? 'text-right' : ''}`}>
                          {t(`exams:examList.examTypes.${exam.exam_type}`)}
                        </span>
                      </div>

                      <div className="text-center">
                        <div className="inline-flex items-center text-sm text-surface-secondary font-medium font-hebrew">
                          <MixedText>{exam.processing_status === 'processing' 
                            ? t('exams:examList.processing')
                            : exam.processing_status === 'failed'
                            ? t('exams:processing.retry')
                            : t('exams:processing.pending')
                          }</MixedText>
                        </div>
                      </div>
                    </CardContent>
                    
                    <CardFooter align="center" className="border-t border-surface-muted/20">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditExam(exam);
                          }}
                          className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-blue/10 hover:text-vibrant-blue flex items-center justify-center"
                          title={t('common:actions.edit')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteExam(exam.id, exam.title);
                          }}
                          disabled={deletingExam === exam.id}
                          className="p-3 md:p-2 rounded-lg transition-all duration-300 hover:bg-vibrant-orange/10 hover:text-vibrant-orange flex items-center justify-center"
                          title={t('exams:examList.delete')}
                        >
                          {deletingExam === exam.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </CardFooter>
                  </SpaceCard>
                )
              ))}

              {/* Add Exam Card - Show last (right) for LTR */}
              {!isRTL && (
                <SpaceCard
                  constellation="ursa"
                  onClick={handleCreateExamClick}
                  className="cursor-pointer border-2 border-dashed border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] min-h-[240px] md:min-h-[280px] flex flex-col items-center justify-center text-center"
                >
                  <div className="p-6 md:p-8 flex flex-col items-center justify-center">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-vibrant-orange/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-vibrant-orange/20 transition-colors duration-300">
                      <Plus className="w-6 h-6 md:w-8 md:h-8 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <CardTitle className={`text-lg md:text-xl mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      🚀 <MixedText>{t('exams:examList.createExam')}</MixedText>
                    </CardTitle>
                    <p className={`text-surface-secondary text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:examList.emptyState.description')}</MixedText>
                    </p>
                  </div>
                </SpaceCard>
              )}
            </div>
          </CardContent>
        </SpaceCard>

        {/* Creation Method Modal */}
        {showCreationModal && (
          <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
            <Card className="w-full max-w-4xl shadow-2xl mx-auto">
              {/* Modal Header */}
              <CardHeader className="border-b border-surface-muted/20">
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`${isRTL ? 'text-right' : ''}`}>
                    <CardTitle className={`text-2xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      ✨ <MixedText>{t('exams:examCreation.chooseMethod.title')}</MixedText>
                    </CardTitle>
                    <CardContent className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{t('exams:examCreation.chooseMethod.subtitle')}</MixedText>
                    </CardContent>
                  </div>
                  <button
                    onClick={() => setShowCreationModal(false)}
                    className="p-2 hover:bg-surface-muted/10 rounded-xl transition-colors duration-200"
                  >
                    <X className="w-6 h-6 text-surface-secondary" />
                  </button>
                </div>
              </CardHeader>

              {/* Modal Content */}
              <CardContent>
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isRTL ? 'space-x-reverse' : ''}`}>
                  {/* Generate with AI Card */}
                  <SpaceCard
                    constellation="orion"
                    onClick={() => {
                      setShowCreationModal(false);
                      navigate(`/exams/ai-generate?course_id=${courseIdNum}`);
                    }}
                    className="cursor-pointer border-2 border-vibrant-orange/30 hover:border-vibrant-orange/50 transition-all duration-300 hover:shadow-lg hover:scale-105 text-center"
                  >
                    <div className="p-6 md:p-8 flex flex-col items-center">
                      <div className="w-20 h-20 bg-vibrant-orange/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-vibrant-orange/20 transition-colors duration-300 relative">
                        <Brain className="w-10 h-10 text-vibrant-orange group-hover:scale-110 transition-transform duration-300" />
                        <Sparkles className="w-4 h-4 text-muted-purple absolute top-2 right-2 group-hover:scale-125 transition-transform duration-300" />
                      </div>
                      <CardTitle className={`text-xl font-bold mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        🛸 <MixedText>{t('exams:examCreation.chooseMethod.generateAI.title')}</MixedText>
                      </CardTitle>
                      <CardContent className={`text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        <MixedText>{t('exams:examCreation.chooseMethod.generateAI.description')}</MixedText>
                      </CardContent>
                      <div className="mt-6">
                        <span className={`inline-flex items-center px-4 py-2 bg-gradient-to-r from-vibrant-orange to-vibrant-orange/80 text-astronaut-white rounded-xl font-medium hover:from-vibrant-orange hover:to-vibrant-orange transition-all duration-300 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                          <Wand2 className="w-4 h-4" />
                          <span className="font-hebrew">
                            <MixedText>{t('exams:examCreation.chooseMethod.generateAI.button')}</MixedText>
                          </span>
                        </span>
                      </div>
                    </div>
                  </SpaceCard>

                  {/* Upload File Card */}
                  <SpaceCard
                    constellation="cassiopeia"
                    onClick={() => {
                      setShowCreationModal(false);
                      navigate(`/exams/create?course_id=${courseIdNum}`);
                    }}
                    className="cursor-pointer border-2 border-muted-purple/30 hover:border-muted-purple/50 transition-all duration-300 hover:shadow-lg hover:scale-105 text-center"
                  >
                    <div className="p-6 md:p-8 flex flex-col items-center">
                      <div className="w-20 h-20 bg-muted-purple/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-muted-purple/20 transition-colors duration-300">
                        <Upload className="w-10 h-10 text-muted-purple group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <CardTitle className={`text-xl font-bold mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        📁 <MixedText>{t('exams:examCreation.chooseMethod.uploadFile.title')}</MixedText>
                      </CardTitle>
                      <CardContent className={`text-sm max-w-xs font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        <MixedText>{t('exams:examCreation.chooseMethod.uploadFile.description')}</MixedText>
                      </CardContent>
                      <div className="mt-6">
                        <span className={`inline-flex items-center px-4 py-2 bg-gradient-to-r from-muted-purple to-muted-purple/80 text-astronaut-white rounded-xl font-medium hover:from-muted-purple hover:to-muted-purple transition-all duration-300 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                          <Upload className="w-4 h-4" />
                          <span className="font-hebrew">
                            <MixedText>{t('exams:examCreation.chooseMethod.uploadFile.button')}</MixedText>
                          </span>
                        </span>
                      </div>
                    </div>
                  </SpaceCard>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Edit Exam Modal */}
      {editingExam && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ background: 'rgba(26, 15, 43, 0.5)' }}>
          <Card className="w-full max-w-lg shadow-2xl mx-4">
            <CardHeader>
              <CardTitle className={`font-hebrew ${isRTL ? 'text-right' : ''}`}>
                {t('common:actions.edit')}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleUpdateExam} className="space-y-5">
              <div>
                <label htmlFor="editExamName" className={`block text-sm font-semibold text-surface-secondary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    📄 {t('exams:examCreation.form.title')} *
                  </span>
                </label>
                <input
                  type="text"
                  id="editExamName"
                  value={editExamName}
                  onChange={(e) => setEditExamName(e.target.value)}
                  placeholder={t('exams:examCreation.form.titlePlaceholder')}
                  className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew ${isRTL ? 'text-right' : ''}`}
                  required
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              <CardFooter className="flex flex-col space-y-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!editExamName.trim()}
                  className="w-full font-hebrew"
                >
                  {t('common:buttons.update')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    setEditingExam(null);
                    setEditExamName('');
                  }}
                  className="w-full font-hebrew"
                >
                  {t('common:buttons.cancel')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {/* Course Dropdown Portal */}
      {showCourseDropdown && !coursesLoading && createPortal(
        <div 
          className="course-dropdown-portal fixed bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] max-h-64 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          <div className="p-2 space-y-1">
            {allCourses.length > 0 ? (
              allCourses.map((courseItem) => (
                <button
                  key={courseItem.id}
                  onClick={() => handleCourseSwitch(courseItem.id)}
                  className={`w-full px-3 py-2 rounded-lg transition-colors font-hebrew text-right ${
                    courseItem.id === courseIdNum
                      ? 'bg-vibrant-orange/10 text-vibrant-orange border border-vibrant-orange/20'
                      : 'bg-white hover:bg-vibrant-orange/5 text-gray-700 hover:text-vibrant-orange border border-transparent hover:border-vibrant-orange/20'
                  }`}
                >
                  <div className={`flex items-center justify-between`}>
                    <span className="truncate">
                      <MixedText>{courseItem.name}</MixedText>
                    </span>
                    {courseItem.id === courseIdNum && (
                      <span className={`text-xs text-vibrant-orange font-medium ml-2`}>
                        <MixedText>
                          {t('exams:examList.switchCourse.current')}
                        </MixedText>
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className={`px-3 py-2 text-sm text-gray-500 font-hebrew text-right`}>
                <MixedText>
                  {t('common:messages.no_courses_found')}
                </MixedText>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </DashboardContainer>
  );
};

export default ExamListView; 